import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import isNum from '@bybeam/platform-lib/utilities/isNum';
import { Document, FieldType, TextInputType } from '@bybeam/platform-types';
import Icon from '@platform-ui-common/components/Icon/index.js';
import UploadPreview from '@platform-ui-common/components/Upload/Preview.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import { formatAddress, formatCurrency } from '@platform-ui-common/utilities/format.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import InfoField from '@platform-ui/application/pages/ApplicationReview/components/InfoField.js';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { isMultiple } from '@platform-ui/utilities/application/fields/dropdown.js';
import { getFieldLabel } from '@platform-ui/utilities/application/fields/index.js';
import { Box, Flex, Text } from '@radix-ui/themes';
import { usePostHog } from 'posthog-js/react';
import { ReactNode } from 'react';
import Markdown from '../Markdown/index.js';
import styles from './AnswerReview.module.css';
import ArrayReview from './components/ArrayReview.js';
import DocumentListReview from './components/DocumentListReview.js';
import { parseFieldValues } from './parseFieldValues.js';
import { FieldAnswer, ReviewAnswerProps, isArray, isObject, isString } from './types.js';

export function DisplayFieldValue<AnswerType extends FieldAnswer>({
  source,
  answer,
  field,
  defaultValue = '-',
}: Omit<ReviewAnswerProps<AnswerType>, 'previousAnswer'>): JSX.Element | null {
  const placeholderData = usePlaceholder();
  const defaultFragment = <>{defaultValue}</>;
  const fieldCopy = (data: string): string => replacePlaceholders(data, placeholderData);

  switch (field.type) {
    case FieldType.Typography:
      // Nothing to display as an "answer"
      return null;

    case FieldType.Calculated:
      // If display is false, hide it entirely
      if (!field.display) return null;
      // If we happen to get an object or array, just show fallback
      if (isObject(answer) || isArray(answer)) return defaultFragment;
      // Calculated currency answers are stored in cents (like Text currency), so
      // format them as dollars rather than showing the raw cent value.
      if (field.display.inputType === TextInputType.Currency) {
        return answer != null && isNum(String(answer)) ? (
          <>{formatCurrency(Number(answer), true)}</>
        ) : (
          defaultFragment
        );
      }
      return <>{answer ?? defaultValue} </>;

    case FieldType.Date:
      return <>{answer ?? defaultValue} </>;

    case FieldType.Dropdown:
      if (isMultiple(field) && isArray(answer)) {
        return <ArrayReview answer={answer} field={field} defaultValue={defaultValue} />;
      }
      if (!isMultiple(field) && !isArray(answer) && !isObject(answer)) {
        return <>{parseFieldValues(field.options, answer, defaultValue)} </>;
      }
      return defaultFragment;

    case FieldType.CheckboxGroup:
      if (!isArray(answer)) return defaultFragment;
      return <ArrayReview answer={answer} field={field} defaultValue={defaultValue} />;

    case FieldType.RadioList: {
      const { label } =
        field.options.find(({ value }) => {
          return typeof answer === 'number' ? answer.toString() === value : answer === value;
        }) || {};
      return label ? <Markdown>{fieldCopy(label)}</Markdown> : defaultFragment;
    }

    case FieldType.Checkbox:
      if (!answer) return null;
      return (
        <InfoField subtitle={fieldCopy(field.copy)}>
          <Icon type="checkCircle" size="MD" />
        </InfoField>
      );

    case FieldType.Text:
      switch (field.inputType) {
        case TextInputType.Currency:
          return answer && isNum(answer as string) ? (
            <>{formatCurrency(Number(answer), true)}</>
          ) : (
            defaultFragment
          );
        case TextInputType.Number:
          // Temporary solution to not parsing SSN numbers. This should be removed when we have a better solution like adding a SSN field type.
          if (field.isSensitive && field.key?.toLowerCase()?.includes('ssn')) return <>{answer}</>;
          return answer && isNum(answer as string) ? (
            <>{Number(answer).toLocaleString()}</>
          ) : (
            defaultFragment
          );
        default:
          if (isObject(answer)) return defaultFragment;
          return isString(answer) ? <>{answer}</> : defaultFragment;
      }

    case FieldType.Address: {
      const matchedAddress = source?.addresses?.find(({ id }) => id === answer);
      return matchedAddress ? <>{formatAddress(matchedAddress)} </> : defaultFragment;
    }

    case FieldType.Document: {
      if (!isArray(answer) || !answer.length || !source?.documents?.length) {
        return defaultFragment;
      }
      const usedDocuments = answer.reduce((allDocs: Document[], documentId: string) => {
        const matchedDocument = source.documents.find(({ id }) => id === documentId);
        if (matchedDocument) allDocs.push(matchedDocument);
        return allDocs;
      }, []);
      if (!usedDocuments?.length) return defaultFragment;
      return (
        <DocumentListReview>
          {usedDocuments.map((document) => (
            <UploadPreview
              key={document.id}
              url={document.previewUrl}
              name={document.filename}
              {...document}
              editable={false}
              loading={false}
            />
          ))}
        </DocumentListReview>
      );
    }

    case FieldType.Complex:
      // We'll handle "complex" nesting and diffs at a higher level.
      // So for the *current* value alone, we can simply show nothing or partial.
      // Alternatively, you could show a read-only version here if you like.
      return null;

    default:
      assertExhaustive(field);
  }
}

// Compare two answers for changes
const isCurrentAnswerDifferentThanPreviousAnswer = (
  currentAnswer: FieldAnswer,
  previousAnswer: FieldAnswer,
): boolean => {
  if (Array.isArray(currentAnswer) && Array.isArray(previousAnswer)) {
    return (
      currentAnswer.length !== previousAnswer.length ||
      currentAnswer.some((val, index) => val !== previousAnswer[index])
    );
  }

  return currentAnswer !== previousAnswer;
};

export function AnswerDiff({
  currentAnswer,
  previousAnswer,
  children,
}: {
  currentAnswer: FieldAnswer;
  previousAnswer: FieldAnswer;
  children: ReactNode;
}) {
  const posthog = usePostHog();
  if (!posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.applicationDiffs)) {
    return null;
  }

  const isDifferent = isCurrentAnswerDifferentThanPreviousAnswer(currentAnswer, previousAnswer);
  return isDifferent ? (
    <Box width="fit-content" py="1" px="2" className={styles.previousAnswer}>
      <Flex direction="column">
        <Text color="gray" weight="bold" size="2">
          Previous response
        </Text>
        <Box>
          <Text as="span" color="gray" size="2">
            {children}
          </Text>
        </Box>
      </Flex>
    </Box>
  ) : null;
}

export default function AnswerReview({
  source,
  answer,
  previousAnswer,
  field,
  showDiff = false,
  defaultValue = '-',
}: ReviewAnswerProps<FieldAnswer>): JSX.Element | null {
  // Handle "Complex" fields by recursing for each subfield
  if (field.type === FieldType.Complex) {
    if (!isArray(answer)) return <>{defaultValue}</>;
    return (
      <>
        {answer.map((entry, i) => (
          <div
            key={`${field.key}-${i}`}
            className="flex flex-wrap gap-2 py-3 border-b border-inputBorder last:border-transparent"
          >
            {field.subFields.map((subField, j) => (
              <div
                key={`${subField.key}-${j}`}
                className="flex flex-grow flex-1 w-min min-w-[150px]"
              >
                <InfoField subtitle={getFieldLabel(subField) ?? subField.key} bold={false}>
                  <AnswerReview
                    source={source}
                    field={subField}
                    answer={entry[subField.key]}
                    previousAnswer={previousAnswer?.[i]?.[subField.key]}
                    defaultValue={defaultValue}
                  />
                </InfoField>
              </div>
            ))}
          </div>
        ))}
        {showDiff && (
          <AnswerDiff currentAnswer={answer} previousAnswer={previousAnswer}>
            <DisplayFieldValue
              source={source}
              field={field}
              answer={previousAnswer}
              defaultValue={defaultValue}
            />
          </AnswerDiff>
        )}
      </>
    );
  }

  // All non-complex fields can reuse DisplayFieldValue.
  return (
    <>
      <DisplayFieldValue
        source={source}
        field={field}
        answer={answer}
        defaultValue={defaultValue}
      />
      {showDiff && (
        <Box pt="1">
          <AnswerDiff currentAnswer={answer} previousAnswer={previousAnswer}>
            <DisplayFieldValue
              source={source}
              field={field}
              answer={previousAnswer}
              defaultValue={defaultValue}
            />
          </AnswerDiff>
        </Box>
      )}
    </>
  );
}
